import { Dictionary } from "~/Types/Common";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { Options } from "~/Types/Options";

let analytics = new AnalyticsBrowser();
let wasInitialized = false;
let delayedEvents: (() => void)[] = [];
let storedOptions: Options;

export const initializeTelemetry = (options: Options) => {
    storedOptions = options;
    if (!options.telemetry.segmentWriteKeyFrontend || wasInitialized) {
        return;
    }

    wasInitialized = true;
    for (const delayedEvent of delayedEvents) {
        delayedEvent();
    }
    analytics.load({ writeKey: options.telemetry.segmentWriteKeyFrontend });
};

export const telemetry = {
    identify: (parameter: IdentifyParameter) => {
        sendWhenReady(() => {
            let traits = {
                isOptiUser: isOptimizelyUser(parameter.email),
                ...parameter,
                ...getRequiredTraits(),
            };

            // we want to use email as userId to try to track users across various products
            analytics.identify(parameter.email, traits);
        });
    },

    group: () => {
        sendWhenReady(() => {
            let traits = {
                hasTurnstile: true,
                name: storedOptions.turnstile.organizationName,
                ...getRequiredTraits(),
            };

            analytics.group(storedOptions.turnstile.masterCustomerId, traits);
        });
    },

    page: (parameter: PageParameter) => {
        sendWhenReady(() => {
            const { category, name, ...otherProperties } = parameter;

            let allProperties = {
                ...getRequiredTraits(),
                ...otherProperties,
            };

            analytics.page(category, name, allProperties);
        });
    },

    track: async (eventName: string, email?: string, properties?: Dictionary<unknown>) => {
        if (!email) {
            email = (await telemetry.getCurrentIdentifiedUserId()) ?? undefined;
        }

        sendWhenReady(() => {
            let allProperties = {
                // we want to use email as userId to try to track users across various products
                userId: email,
                isOptiUser: !email ? false : isOptimizelyUser(email),
                ...getRequiredTraits(),
                ...(properties || {}),
            };

            analytics.track(eventName, allProperties);
        });
    },

    reset: () => {
        analytics.reset();
    },

    getCurrentIdentifiedUserId: async () => {
        return (await analytics.user()).id();
    },
};

type IdentifyParameter = {
    email: string;
    firstName: string;
    lastName: string;
    id: string;
};

type PageParameter = {
    category?: string;
    name?: string;
    path?: string;
    url?: string;
    referrer?: string;
};

let sendWhenReady = (event: () => void) => {
    if (!wasInitialized) {
        delayedEvents.push(event);
    } else {
        event();
    }
};

let getRequiredTraits = () => {
    return {
        productName: "Monetize",
        subProductName: "Configured Commerce",
        groupId: storedOptions.turnstile.masterCustomerId,
        organizationId: storedOptions.turnstile.organizationId,
        instanceId: storedOptions.turnstile.instanceId,
        instanceName: storedOptions.turnstile.organizationName,
    };
};

let isOptimizelyUser = (emailAddress: string) => {
    const loweredEmail = emailAddress?.toLowerCase() ?? "";

    return (
        loweredEmail.endsWith("@insitesoft.com") ||
        loweredEmail.endsWith("@episerver.com") ||
        loweredEmail.endsWith("@optimizely.com")
    );
};
